<template>
  <v-card :disabled="isSubDisabled || !canWrite">
    <user-badge :have-permissions="!isSubDisabled" :read-only="!canWrite"/>
    <v-col cols="12" v-if="!canWrite" no-gutters
      class="blue-grey--text text--lighten-1 pt-0 pb-0">
      <v-icon color="red">mdi-alert-box-outline</v-icon>
      {{ $t('perms.nochange') }}
    </v-col>
    <v-card-title primary class="title grey--text pb-0">
      <v-switch class="fm-switch" v-model="enabled" :label="$t('settings.fme.title')">
        <template v-slot:prepend>
          <v-icon :color="enabled ? 'green' : 'grey'">mdi-call-split</v-icon>
        </template>
        <template v-slot:append>
          <cp-help i18n="followme.enable"/>
        </template>
      </v-switch>
    </v-card-title>
    <v-card-text v-if="enabled">
      <fieldset class="rounded-lg pa-4">
        <legend>{{ $t('settings.fme.labels.numbers') }}</legend>
        <followme-numbers :nums="numbers" :enabled="enabled" @numbers-valid="setNumbersFormValid"/>
      </fieldset>
      <v-switch class="fm-show-more mt-4" dense v-model="showMore"
        :disabled="!enabled" :label="$t('settings.fme.showMore')">
      </v-switch>
      <div v-show="showMore" class="mt-4">
        <v-switch class="fm-confirm mt-0" dense v-model="answerConfirm"
          :disabled="!enabled" :label="$t('settings.fme.confirm')">
          <template v-slot:append>
            <cp-help i18n="followme.confirm"/>
          </template>
        </v-switch>
        <v-switch class="fm-answered-elsewhere mt-0" dense
          v-model="markAnswElsewhere" :disabled="!enabled"
          :label="$t('settings.fme.markanswered')">
          <template v-slot:append>
            <cp-help i18n="followme.markanswered"/>
          </template>
        </v-switch>
        <followme-ring-strategies class="followme-ring-strategies"/>
        <time-cond />
        <v-row>
          <v-col cols="6" xl="6" sm="12">
            <fieldset class="rounded-lg ma-2 pa-4">
              <legend>{{ $t('settings.fme.labels.cid') }}</legend>
              <v-text-field class="fm-prefix"
                v-model="clidPrefix" :disabled="!enabled" :label="$t('settings.fme.prefix')" :placeholder="$t('settings.fme.prefixPlaceholder')">
                <template v-slot:append-outer>
                  <cp-help i18n="followme.prefix"/>
                </template>
              </v-text-field>
              <caller-ID :disabled="!enabled" @callerid-valid="setCallerIDFormValid"/>
            </fieldset>
          </v-col>
          <v-col cols="6" xl="6" sm="12">
            <fieldset class="rounded-lg ma-2 pa-4">
              <legend>{{ $t('settings.fme.labels.timeouts') }}</legend>
              <followme-tout
                help="followme.toutFirst"
                @change="updateToutFirst"
                @timeout-valid="setDelayFormValid"
                :tout="toutFirst"
                class="timeout-first"
                :label="$t('settings.fme.ringFirst')"
                :enabled="enabled" />
              <followme-tout
                help="followme.toutAll"
                @change="updateToutAll"
                @timeout-valid="setTimeoutFormValid"
                class="timeout-all"
                :tout="toutAll"
                :label="$t('settings.fme.ringAll')"
                :enabled="enabled" />
            </fieldset>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-icon color="red" class="mr-3" v-if="isSubDisabled" small>mdi-lock</v-icon>
      <v-btn text @click="save" :disabled="!formValid || !canWrite" class="save-btn">
        {{ $t('settings.fwd.save') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import FollowmeNumbers from './numbers'
import FollowmeRingStrategies from './ring_strategies'
import FollowmeTout from './timeout'
import CallerID from './callerid'
import TimeCond from './time_cond'
import UserBadge from '../user_badge'
import events from 'Store/events'
import * as RMQClient from '@/rmqclient'
import API from '@/api'

export default {
  name: 'settings-followme',
  data () {
    return {
      showMore: false,
      numbersFormValid: true,
      calleridValid: true,
      delayValid: true,
      timeoutValid: true
    }
  },
  components: { FollowmeNumbers, FollowmeTout, CallerID, TimeCond, UserBadge, FollowmeRingStrategies },
  methods: {
    ...mapActions({
      updateEnabled: 'settings/setFollowmeEnabled',
      updateConfirm: 'settings/setFollowmeConfirm',
      updateMarkAnswered: 'settings/setFollowmeMarkAnswered',
      updatePrefix: 'settings/setFollowmePrefix',
      updateToutFirst: 'settings/setFollowmeToutFirst',
      updateToutAll: 'settings/setFollowmeToutAll',
      publishFollowme: 'sipua/publishFollowme'
    }),
    save: function () {
      if (this.enabled && this.fmeData?.numbers?.length < 1) {
        events.pubError({ key: 'settings.fme.noData' })
        return
      }
      events.mask()
      API.settingsFollowMe(this.ownerID, this.postData()).then(() => {
        const key = 'settings.notify.fme' + (this.enabled ? 'Saved' : 'Off')
        events.pubNotify({ text: key, status: 'success' })
        this.publishFollowme(this.enabled)
        RMQClient.sendCmd({
          route: 'DIRECT',
          action: 'BUDDYPREF',
          pref: 'FOLLOWME',
          username: this.sipUser,
          realm: this.realm,
          enabled: this.enabled
        })
      }).catch(err => {
        this.dbg(err)
        this.dbg(err.response)
        events.pubError(API.getException(err) || 'API failed to update Follow-Me.')
      }).finally(events.unmask)
    },
    postData: function () {
      return {
        enabled: this.enabled,
        to: this.fmeData.numbers.join(','),
        delay: this.fmeData.toutfirst,
        timeout: this.fmeData.toutall,
        clid_prefix: this.fmeData.prefix,
        time_condition_id: this.fmeData.timeConditionID,
        prompt: this.fmeData.confirm ? '1' : '0',
        // stub values
        extern_cid: this.fmeData.externCID,
        extern_cid_name: this.fmeData.externCIDName,
        extern_cid_num: this.fmeData.externCIDNum,
        strategy: this.fmeData.strategy,
        mark_answered_elsewhere: this.fmeData.markAnswered
      }
    },
    setNumbersFormValid (valid) {
      this.numbersFormValid = valid
    },
    setCallerIDFormValid (valid) {
      this.calleridValid = valid
    },
    setDelayFormValid (valid) {
      this.delayValid = valid
    },
    setTimeoutFormValid (valid) {
      this.timeoutValid = valid
    }
  },
  computed: {
    ...mapGetters({
      fmeData: 'settings/followme',
      fmeWrite: 'permissions/fmeWrite',
      fmeWriteFor: 'permissions/fmeWriteFor',
      sipUser: 'sipua/exten',
      activeSubAccount: 'settings/activeSubAccount',
      ownerID: 'settings/ownerID',
      isOwnerCurUser: 'settings/isOwnerCurUser',
      realm: 'settings/realm'
    }),
    formValid () {
      return this.numbersFormValid && this.calleridValid && this.delayValid && this.timeoutValid
    },
    isSubDisabled: function () {
      if (!this.activeSubAccount || this.isOwnerCurUser) {
        return false
      }
      return !this.activeSubAccount.features?.includes('follow_me')
    },
    canWrite: function () {
      if (!this.activeSubAccount || this.isOwnerCurUser) {
        return this.fmeWrite
      }
      return this.fmeWriteFor(this.activeSubAccount?.subscriber_id)
    },
    enabled: {
      get: function () {
        return this.fmeData.enabled
      },
      set: function (val) {
        this.updateEnabled(val)
      }
    },
    answerConfirm: {
      get: function () {
        return this.fmeData.confirm
      },
      set: function (val) {
        this.updateConfirm(val)
      }
    },
    markAnswElsewhere: {
      get: function () {
        return this.fmeData.markAnswered
      },
      set: function (val) {
        this.updateMarkAnswered(val)
      }
    },
    clidPrefix: {
      get: function () {
        return this.fmeData.prefix
      },
      set: function (val) {
        this.updatePrefix(val)
      }
    },
    toutFirst: {
      get: function () {
        return this.fmeData.toutfirst
      },
      set: function (val) {
        this.updateToutFirst(val)
      }
    },
    toutAll: {
      get: function () {
        return this.fmeData.toutall
      },
      set: function (val) {
        this.updateToutAll(val)
      }
    },
    numbers: {
      get: function () {
        return this.fmeData.numbers
      },
      set: function (val) {
      }
    }
  }
}
</script>

<style scoped>
</style>
